import type { ResolvedAssets, UploadedAsset } from './api/assets';

export type NodeRunStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELED';
export type RecipeRunStatus = 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELED';
export type BatchStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELED';

export type WorkflowRunLifecycleStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELED';

export const TERMINAL_WORKFLOW_RUN_STATUSES: readonly WorkflowRunLifecycleStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELED',
];

export interface BatchRun {
  batchId: string;
  recipeId: string;
  recipeRuns: RecipeRun[];
  // Authoritative lifecycle for async runs; absent for legacy synchronous batches (state is derived
  // from recipeRuns instead).
  status?: WorkflowRunLifecycleStatus;
  /**
   * Nodes this run executes, sent for async runs. A queued run has no node runs yet, so this is the
   * only way the canvas knows which nodes are involved before the worker fans out.
   */
  executionNodeIds?: string[];
  userRemainingCredits?: number;
  remainingCredits?: number;
  isClearedInTaskManager?: boolean;
  /** Client-side arrival time, used to order batches that have no run to date them by. */
  firstSeenAt?: number;
  isViewed?: boolean;
  isDynamicCost?: boolean;
  resolvedAssets?: ResolvedAssets;
  /**
   * The workflow's latest `graphUpdatedAt`, including the results this status delivers. Sent only with
   * `graph-updated-at-save-conflict` on.
   */
  graphUpdatedAt?: string | null;
}

export interface NodeRun {
  error?: string;
  /**
   * From linked prediction when the API includes it: ISO time when platform credits were refunded; `null` when failed and not refunded.
   * Omitted if there is no prediction row for this node run.
   */
  refundedAt?: string | null;
  id: string;
  input: Record<string, any>;
  nodeId: string;
  result: UploadedAsset[];
  runId: string;
  status: NodeRunStatus;
  progress: number;
  inputIndex?: number;
}

export interface RecipeRun {
  batchId: string;
  error?: string;
  id: string;
  nodeRuns: NodeRun[];
  runIndexInBatch: number;
  startTime: Date;
  status: RecipeRunStatus;
  inputIndex: number;
}
